import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { AuthContext } from '../Auth/AuthContext';
import useSettings from '../../hooks/useSettings';

const SettingsContext = createContext();

export const useSettingsContext = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettingsContext must be used within a SettingsProvider');
  }
  return context;
};

export const SettingsProvider = ({ children }) => {
  const { user } = useContext(AuthContext);
  const { getAll, update } = useSettings();

  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastFetch, setLastFetch] = useState(null);

  // Cache TTL - 5 minutos
  const CACHE_TTL = 5 * 60 * 1000;

  const fetchSettings = useCallback(async (forceRefresh = false) => {
    // Se já tem dados em cache e não está forçando refresh, retorna cache
    if (!forceRefresh && settings.length > 0 && lastFetch) {
      const now = new Date().getTime();
      if (now - lastFetch < CACHE_TTL) {
        return settings;
      }
    }

    if (!user?.companyId) {
      setSettings([]);
      setLoading(false);
      return [];
    }

    try {
      setLoading(true);
      setError(null);

      const params = { companyId: user.companyId };
      const response = await getAll(params);

      // Safe handling of settings response
      const allSettings = Array.isArray(response) ? response : (response?.settings || []);

      setSettings(allSettings);
      setLastFetch(new Date().getTime());

      return allSettings;
    } catch (err) {
      console.error('Error fetching settings:', err);
      setError(err.message || 'Erro ao carregar configurações');
      setSettings([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, [user?.companyId, getAll]);

  // Carregar configurações quando o usuário/empresa muda
  useEffect(() => {
    if (user?.companyId) {
      fetchSettings();
    }
  }, [user?.companyId]); // Apenas user.companyId como dependência

  // Função para buscar uma configuração específica
  const getSetting = useCallback((key, defaultValue = null) => {
    const setting = settings.find(s => s.key === key);
    return setting ? setting.value : defaultValue;
  }, [settings]);

  // Função para atualizar uma configuração
  const updateSetting = useCallback(async (key, value) => {
    try {
      const updatedSetting = await update({ key, value });

      // Atualizar cache local
      setSettings(prevSettings => {
        const newSettings = [...prevSettings];
        const index = newSettings.findIndex(s => s.key === key);

        if (index >= 0) {
          newSettings[index] = { ...newSettings[index], value };
        } else {
          newSettings.push({ key, value });
        }

        return newSettings;
      });

      return updatedSetting;
    } catch (err) {
      console.error('Error updating setting:', err);
      setError(err.message || 'Erro ao atualizar configuração');
      throw err;
    }
  }, [update]);

  // Função para invalidar cache
  const refreshSettings = useCallback(() => {
    return fetchSettings(true);
  }, [fetchSettings]);

  const value = {
    settings,
    loading,
    error,
    getSetting,
    updateSetting,
    refreshSettings,
    fetchSettings,
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};

export default SettingsContext;